import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { useCurrentDateRange } from 'hooks/dateRange';
import HeaderMenu from 'views/Production/HeaderMenu/index';
import Loader from './Loader';
import AllFleet from './AllFleet';
import Hauler from './Hauler';
import UtilizationHandler from 'handlers/utilization.handler';
import { exportToExcelForUtilization } from 'utils/exports/excel';
import { calculateDaysDifference } from 'views/FuelAnalytics/IdleDashboard/utils';
import {
  convertStdDateString,
  formatQuickRange,
  formatDateRangeForFileName,
} from 'utils/helpers/dateTime';
import { splitCamelCase } from 'utils/helpers/string.manipulation';
import { type ChartSource } from 'views/Production/AllFleet/utils';
import useTimeDisplayMode, { useShiftHours } from 'hooks/useTimeDisplayMode';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { getAssetTypeDisplayName } from 'utils/constants/assetDisplayNames';
import { getStartAndEndDateFromRange } from 'views/FuelUsage/utils';
import { exportAnalyticsReport } from 'utils/exports/analyticsReportExports';
import useShiftConfig from 'hooks/useShiftConfig';
import { useTranslation } from 'react-i18next';
import ComingSoonModal from 'components/ComingSoonModal';

const Utilization = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const typeReceived = useMemo(() => location.state?.type, [location]);
  const dispatch = useAppDispatch();
  const utilizationHandler = new UtilizationHandler();

  const { unitSystem } = useCustomerInfo();
  const { isMultiShift } = useShiftConfig();
  const assetOverview = useAppSelector(
    (state) => state.assetReducer.assetOverView
  );

  const data = useAppSelector((state) => state.utilizationReducer.data);

  const loadingStates = useAppSelector(
    (state) => state.utilizationReducer.loadingStates
  );

  const isPrinting = useAppSelector(
    (state) => state.exportDataReducer.isPrinting
  );

  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const defaultValue = useMemo(() => {
    return typeReceived
      ? typeReceived === 'Haul Truck'
        ? 1
        : typeReceived === 'Loader'
        ? 2
        : 0
      : 0;
  }, [typeReceived]);

  const [value, setValue] = useState(defaultValue);
  const [isSelected, setIsSelected] = useState('Today');
  const [isCustomDateChanged, setIsCustomDateChanged] = useState(false);
  const [aggregation, setAggregation] = useState<string>('Daily');
  const [shiftWiseAnalysis, setShiftWiseAnalysis] =
    useState<boolean>(isMultiShift);
  const [shiftNumber, setShiftNumber] = useState<number>(1);
  const [utilizationType, setUtilizationType] = useState('engineHours');
  const [detailsVal, setDetailsVal] = useState<Record<string, unknown>>({});
  const [analyticsData, setAnalyticsData] = useState<any[]>();
  const [hoursBreakDown, setHoursBreakDown] = useState<{
    hauler: any;
    loader: any;
  }>({
    hauler: null,
    loader: null,
  });

  const [userSelections, setUserSelections] = useState({});
  const [suggestionData, setSuggestionData] = useState({});
  const [fuelConsumption, setFuelConsumption] = useState<{
    hauler: any;
    loader: any;
  }>({
    hauler: null,
    loader: null,
  });

  const shiftHours = useShiftHours(shiftWiseAnalysis, shiftNumber);

  // const { startDate, endDate, setStartDate, setEndDate, today } =
  //   useCurrentDateRange({});

  const [intialDateRange] = useState({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  const { startDate, endDate, setStartDate, setEndDate, today } =
    useCurrentDateRange({
      initialDate: new Date(intialDateRange.startDate),
    });

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };
  const [exportTo, setExportTo] = useState('');
  const [, setExportClicked] = useState(false);
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);

  const [selectedChartSource, setSelectedChartSource] =
    useState<ChartSource>('Last 7 Days'); // This could be moved to redux to avoid prop drilling

  useEffect(() => {
    if (
      aggregation === 'Monthly' &&
      isSelected === 'Custom' &&
      startDate &&
      endDate
    ) {
      const newStartDate = dayjs(startDate).startOf('month').toDate();
      const newEndDate = dayjs(endDate).endOf('month').toDate();

      const startNeedsUpdate =
        dayjs(startDate).format('YYYY-MM-DD') !==
        dayjs(newStartDate).format('YYYY-MM-DD');
      const endNeedsUpdate =
        dayjs(endDate).format('YYYY-MM-DD') !==
        dayjs(newEndDate).format('YYYY-MM-DD');

      if (startNeedsUpdate || endNeedsUpdate) {
        if (startNeedsUpdate) setStartDate(newStartDate);
        if (endNeedsUpdate) setEndDate(newEndDate);
      }
    }
  }, [aggregation, isSelected, startDate, endDate, setStartDate, setEndDate]);

  const handleExportClick = async (returnType: 'excel' | 'pdf') => {
    try {
      const getDailyProductiveHoursRange = (
        selectedChartSource: ChartSource
      ) => {
        switch (selectedChartSource) {
          case 'Last 7 Days':
            return '7D';
          case 'Last 14 Days':
            return '14D';
          case 'Last 4 Weeks':
            return '1M';
          case 'Last 6 Months':
            return '6M';
          default:
            return '7D';
        }
      };

      const dailyProductiveHoursRange =
        getDailyProductiveHoursRange(selectedChartSource);

      await exportAnalyticsReport({
        startDate,
        endDate,
        range: isSelected,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        assetTypeValue: value,
        utilizationType,
        dispatch,
        fileNamePrefix: 'Utilization',
        dailyProductiveHoursRange,
        returnType,
        suggestionData: returnType === 'excel' ? suggestionData : undefined,
        getReportBytes: async (
          formattedStartDate: string,
          formattedEndDate: string,
          range: string,
          aggregation: string,
          shiftWiseAnalysis: boolean,
          shiftNumber: number,
          assetType: string,
          utilizationType: string,
          returnType: 'excel' | 'pdf',
          dailyProductiveHoursRange?: string,
          suggestionData?: Record<string, unknown>
        ) =>
          await utilizationHandler.getUtilizationReport(
            formattedStartDate,
            formattedEndDate,
            range,
            aggregation,
            shiftWiseAnalysis,
            shiftNumber,
            assetType,
            utilizationType,
            returnType,
            dailyProductiveHoursRange,
            shiftHours,
            suggestionData
          ),
        errorMessage: t('error_calling_utilization_report_api'),
      });
    } catch (error) {
      // Error is already logged in the utility function
    }
  };

  const isNotLoadingData = useMemo(
    () =>
      Object.entries(loadingStates)
        .filter(
          ([key]) => key !== 'isLoadingTarget' && key !== 'isLoadingSuggestions'
        )
        .every(([, value]) => !value),
    [loadingStates]
  );
  const fetchData = useCallback(
    async (
      range: string,
      startDate: Date,
      endDate: Date,
      aggregation: string
    ) => {
      const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
      const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
      const shouldSendDate = isSelected === 'Custom' || isSelected === 'Today';

      if (isNotLoadingData) {
        await utilizationHandler.get(
          range,
          formattedStartDate,
          formattedEndDate,
          shouldSendDate,
          false,
          value === 2 ? 'loader' : value === 1 ? 'loader' : 'all',
          ['1Y', 'YTD', 'Custom'].includes(isSelected)
            ? aggregation.toLowerCase()
            : 'daily',
          shiftWiseAnalysis,
          shiftNumber,
          utilizationType
        );
      }
    },
    [
      isSelected,
      isNotLoadingData,
      value,
      shiftWiseAnalysis,
      shiftNumber,
      utilizationType,
    ]
  );

  const handleFetchCustomDateRangeData = useCallback(
    ({ aggregation }: { aggregation: string }) => {
      void fetchData(isSelected, startDate, endDate, aggregation);
    },
    [fetchData, isSelected, startDate, endDate]
  );

  const handleFetchCustomOnAggregationChange = useCallback(
    ({
      aggregation,
      startDate,
      endDate,
    }: {
      aggregation: string;
      startDate: Date;
      endDate: Date;
    }) => {
      void fetchData(isSelected, startDate, endDate, aggregation);
    },
    [fetchData, isSelected]
  );

  useEffect(() => {
    const title = '';
    if (exportTo === 'pdf') {
      void handleExportClick('pdf');
    }
    if (exportTo === 'excel') {
      void handleExportClick('excel');
      // const fileNameWithDateTime = `Utilization-Dashboard-for-${getAssetTypeDisplayName(
      //   value
      // )}-${formatDateRangeForFileName(startDate, endDate)}`;
      // exportToExcelForUtilization(
      //   {
      //     ...data.allFleet,
      //     fleetSummary: detailsVal,
      //     value: value === 2 ? 'loader' : value === 1 ? 'hauler' : 'all fleet',
      //   },
      //   fileNameWithDateTime,
      //   `${title}`,
      //   unitSystem,
      //   analyticsData as any[],
      //   hoursBreakDown as {
      //     hauler: any;
      //     loader: any;
      //   },
      //   {
      //     ...userSelections,
      //     quickrange:
      //       isSelected === 'Custom'
      //         ? `${calculateDaysDifference(
      //             startDate,
      //             endDate
      //           )} Days from ${convertStdDateString(startDate)} ${
      //             shiftWiseAnalysis ? 'using shift wise analysis ' : ''
      //           }with utilization by ${splitCamelCase(
      //             utilizationType
      //           )} and ${aggregation} aggregation`
      //         : `${formatQuickRange(isSelected)} ${
      //             shiftWiseAnalysis ? 'using shift wise analysis ' : ''
      //           }with utilization by ${splitCamelCase(utilizationType)}`,
      //   },
      //   suggestionData,
      //   fuelConsumption,
      //   {
      //     status: shiftWiseAnalysis,
      //     value: shiftNumber,
      //     preferredTimeDisplayMode,
      //     timezone,
      //     startDate,
      //     endDate,
      //   },
      //   customerCode,
      //   {
      //     startDate,
      //     endDate,
      //   }
      // );
    }
  }, [exportTo]);

  useEffect(() => {
    if (isSelected === 'Custom') {
      if (!isCustomDateChanged) {
        setIsCustomDateChanged(true);
      }
      // if (isCustomDateChanged && startDate && endDate) {
      //   void fetchData(isSelected, startDate, endDate);
      // }
    } else if (startDate && endDate) {
      setIsCustomDateChanged(false);
      void fetchData(isSelected, startDate, endDate, aggregation);
    }
  }, [
    isSelected,
    startDate,
    endDate,
    aggregation,
    shiftWiseAnalysis,
    shiftNumber,
    utilizationType,
  ]);

  useEffect(() => {
    if (isSelected === 'Custom') {
      if (aggregation === 'Monthly') {
        setStartDate(dayjs(startDate).startOf('month').toDate());
        setEndDate(dayjs(endDate).endOf('month').toDate());
      }
    }
  }, [isSelected, aggregation]);

  return (
    <>
      <HeaderMenu
        value={value}
        handleChange={handleChange}
        handleExport={setExportClicked}
        setExportTo={setExportTo}
        exportTo={exportTo}
        loadingData={isNotLoadingData}
        isPrinting={isPrinting}
        onAiAnalysis={() => {
          setIsAiModalOpen(true);
        }}
      />
      <ComingSoonModal
        open={isAiModalOpen}
        onClose={() => {
          setIsAiModalOpen(false);
        }}
      />
      <div id="utilization-overview">
        <Loader
          value={value}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          assetOverview={assetOverview}
          loadingStates={loadingStates}
          aggregation={aggregation}
          setAggregation={setAggregation}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          utilizationType={utilizationType}
          setUtilizationType={setUtilizationType}
          setDetailsVal={setDetailsVal}
          setAnalyticsData={setAnalyticsData}
          setHoursBreakDown={(value: any) => {
            setHoursBreakDown((prev) => ({ ...prev, loader: value }));
          }}
          setUserSelections={setUserSelections}
          setSuggestionData={setSuggestionData}
          setFuelConsumption={(value: any) => {
            setFuelConsumption((prev) => ({ ...prev, loader: value }));
          }}
          selectedChartSource={selectedChartSource}
          setSelectedChartSource={setSelectedChartSource}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
        />
        <Hauler
          value={value}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          assetOverview={assetOverview}
          aggregation={aggregation}
          setAggregation={setAggregation}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          utilizationType={utilizationType}
          setUtilizationType={setUtilizationType}
          setDetailsVal={setDetailsVal}
          setAnalyticsData={setAnalyticsData}
          setHoursBreakDown={(value: any) => {
            setHoursBreakDown((prev) => ({ ...prev, hauler: value }));
          }}
          setUserSelections={setUserSelections}
          setSuggestionData={setSuggestionData}
          setFuelConsumption={(value: any) => {
            setFuelConsumption((prev) => ({ ...prev, hauler: value }));
          }}
          selectedChartSource={selectedChartSource}
          setSelectedChartSource={setSelectedChartSource}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
        />
        <AllFleet
          value={value}
          isSelected={isSelected}
          setIsSelected={setIsSelected}
          handleDateChange={handleDateChange}
          setStartDate={setStartDate}
          startDate={startDate}
          setEndDate={setEndDate}
          endDate={endDate}
          yesterday={today}
          assetOverview={assetOverview}
          aggregation={aggregation}
          setAggregation={setAggregation}
          shiftWiseAnalysis={shiftWiseAnalysis}
          setShiftWiseAnalysis={setShiftWiseAnalysis}
          shiftNumber={shiftNumber}
          setShiftNumber={setShiftNumber}
          isPrinting={isPrinting}
          utilizationType={utilizationType}
          setUtilizationType={setUtilizationType}
          setDetailsVal={setDetailsVal}
          setAnalyticsData={setAnalyticsData}
          setHoursBreakDown={setHoursBreakDown}
          setUserSelections={setUserSelections}
          setSuggestionData={setSuggestionData}
          selectedChartSource={selectedChartSource}
          setSelectedChartSource={setSelectedChartSource}
          handleFetchCustomDateRangeData={handleFetchCustomDateRangeData}
          handleFetchCustomOnAggregationChange={
            handleFetchCustomOnAggregationChange
          }
        />
      </div>
    </>
  );
};

export default Utilization;
